import React, { useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../components/content_wrapper/header";
import CustomLoader from "../../components/customLoader";
import NoRecordFound from "../../components/noRecordFound";
import Pagination from "../../utils/pagination";
import {
  useGetDisputedKeys,
  useResolveDispute,
} from "../../hooks/disputes/useGetDisputedKeys";

// NB: the API returns snake_case; the axios response interceptor camelCases it,
// so every field below is read camelCase (stripe_dispute_id -> stripeDisputeId,
// evidence_due_by -> evidenceDueBy, key_id -> keyId, key_name -> keyName, ...).

const STATE_TABS = [
  { key: "open", label: "Needs attention" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];

const REASON_LABEL: Record<string, string> = {
  fraudulent: "Fraudulent",
  product_not_received: "Product not received",
  product_unacceptable: "Product unacceptable",
  subscription_canceled: "Subscription cancelled",
  duplicate: "Duplicate",
  credit_not_processed: "Credit not processed",
  unrecognized: "Unrecognised",
  general: "General",
};

function StatusPill({ status }: { status?: string }) {
  const map: Record<string, string> = {
    won: "#27ae60",
    lost: "#e74c3c",
    needs_response: "#e67e22",
    warning_needs_response: "#e67e22",
    under_review: "#2980b9",
    warning_under_review: "#2980b9",
    warning_closed: "#7f8c8d",
  };
  const colour = map[status || ""] || "#7f8c8d";
  return (
    <span
      style={{
        background: colour,
        color: "#fff",
        padding: "2px 9px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
        textTransform: "capitalize",
      }}
    >
      {String(status || "").replace(/_/g, " ")}
    </span>
  );
}

function Deadline({ r }: { r: any }) {
  if (r.evidenceDueBy == null) return <span className="text-muted">—</span>;
  const when = moment.unix(r.evidenceDueBy);
  if (r.responseOverdue) {
    return (
      <span style={{ color: "#e74c3c", fontWeight: 700 }}>
        Overdue — {when.format("MMM D, HH:mm")}
      </span>
    );
  }
  const urgent = r.hoursToRespond != null && r.hoursToRespond <= 48;
  return (
    <span style={{ color: urgent ? "#e67e22" : "#333", fontWeight: urgent ? 700 : 400 }}>
      {when.fromNow()} ({when.format("MMM D, HH:mm")})
    </span>
  );
}

const DisputesScreen: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [state, setState] = useState("open");

  const { data, isLoading, refetch } = useGetDisputedKeys({ page, limit, state });
  const resolve = useResolveDispute();

  const rows: any[] = data?.data ?? [];
  const pagination = data?.pagination ?? { pages: 1 };

  const openCount = rows.filter((r) => !r.adminResolved).length;
  const overdueCount = rows.filter((r) => r.responseOverdue && !r.adminResolved).length;
  const totalExposure = rows
    .filter((r) => !r.adminResolved)
    .reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const handleResolve = (r: any) => {
    const note = window.prompt(
      `Mark dispute ${r.stripeDisputeId} as handled?\n\nOptional note (e.g. "evidence submitted in Stripe"):`,
      ""
    );
    if (note === null) return; // cancelled
    resolve.mutate(
      { id: r._id, note: note || undefined },
      {
        onSuccess: () => {
          toast.success("Dispute marked resolved");
          refetch();
        },
        onError: () => {
          toast.error("Could not update the dispute");
        },
      }
    );
  };

  return (
    <div className="main-wrapper">
      <Header title="Disputes" />
      <div className="content-wrapper">
        <CustomLoader loader={isLoading} />
        <section className="main-section spacer">
          <div className="container">
            <p className="text-muted mb-3">
              Stripe chargebacks. A new dispute restricts the key and lands here; respond
              in Stripe <strong>before the evidence deadline</strong> or it is lost by
              default. Use <em>Mark resolved</em> once you have submitted evidence or
              decided how to handle a lost one.
            </p>

            {/* Summary */}
            <div className="row g-3 mb-3">
              <div className="col-6 col-md-3">
                <div className="card h-100" style={{ borderLeft: "4px solid #e67e22" }}>
                  <div className="card-body py-3 px-3">
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>
                      Needs attention (page)
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{openCount}</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card h-100" style={{ borderLeft: "4px solid #e74c3c" }}>
                  <div className="card-body py-3 px-3">
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>
                      Overdue (page)
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: overdueCount ? "#e74c3c" : undefined }}>
                      {overdueCount}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card h-100" style={{ borderLeft: "4px solid #8e44ad" }}>
                  <div className="card-body py-3 px-3">
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>
                      At risk (page)
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>£{totalExposure.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-pills mb-3" style={{ gap: 6 }}>
              {STATE_TABS.map((t) => (
                <li className="nav-item" key={t.key}>
                  <button
                    className={`nav-link ${state === t.key ? "active" : ""}`}
                    onClick={() => {
                      setState(t.key);
                      setPage(1);
                    }}
                  >
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>

            {!isLoading && rows.length === 0 ? (
              <NoRecordFound />
            ) : (
              <div className="table-responsive wrapper-card">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Key / Owner</th>
                      <th>Amount</th>
                      <th>Reason</th>
                      <th>Status</th>
                      <th>Respond by</th>
                      <th>Opened</th>
                      <th>Dispute ID</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r._id}
                        style={
                          r.responseOverdue && !r.adminResolved
                            ? { background: "#fdecea" }
                            : undefined
                        }
                      >
                        <td>
                          <div className="fw-semibold">{r.keyId?.keyName || "—"}</div>
                          <div className="text-muted small">
                            {r.ownerId?.name || "—"}
                            {r.ownerId?.email ? ` · ${r.ownerId.email}` : ""}
                          </div>
                          {r.keyId?.billingStatus && (
                            <div className="text-muted small text-capitalize">
                              key: {r.keyId.billingStatus}
                            </div>
                          )}
                        </td>
                        <td className="fw-semibold" style={{ whiteSpace: "nowrap" }}>
                          {(r.currency || "GBP") === "GBP" ? "£" : ""}
                          {Number(r.amount ?? 0).toFixed(2)}
                          {(r.currency || "GBP") !== "GBP" ? ` ${r.currency}` : ""}
                        </td>
                        <td>{REASON_LABEL[r.reason] || r.reason || "—"}</td>
                        <td>
                          <StatusPill status={r.status} />
                          {r.hasEvidence && (
                            <div className="text-muted small mt-1">
                              <i className="fa-solid fa-paperclip me-1" />
                              evidence submitted
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <Deadline r={r} />
                        </td>
                        <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                          {r.openedAt ? moment.unix(r.openedAt).format("MMM D, YYYY") : "—"}
                        </td>
                        <td>
                          <code className="small" style={{ wordBreak: "break-all" }}>
                            {r.stripeDisputeId || "—"}
                          </code>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {r.keyId?._id && (
                            <button
                              className="btn btn-outline-secondary btn-sm me-1"
                              onClick={() =>
                                navigate("/keymanagementdetail", {
                                  state: { id: r.keyId._id },
                                })
                              }
                            >
                              Key
                            </button>
                          )}
                          {r.adminResolved ? (
                            <span className="badge bg-success">Resolved</span>
                          ) : (
                            <button
                              className="btn btn-outline-primary btn-sm"
                              disabled={resolve.isLoading}
                              onClick={() => handleResolve(r)}
                            >
                              Mark resolved
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination.pages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={pagination.pages}
                handlePrevPage={() => setPage((p) => Math.max(1, p - 1))}
                handleNextPage={() => setPage((p) => p + 1)}
                handleRowLimitChange={(n: number) => {
                  setLimit(n);
                  setPage(1);
                }}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DisputesScreen;
